/** @file Store module for managing editor sessions. */
import { ListEditorSessionsCommand, } from '@codegrade/apiv2-client';
import { Maybe } from '@codegrade/maybe';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import { FileTree } from '@/models/editor/file_tree';
import { Submission } from '@/models/editor/submission';
import * as utils from '@/utils';
const storeBuilder = getStoreBuilder();
/** @returns The initial state. */
function makeInitialState() {
    return {
        sessions: {},
        loaders: {},
    };
}
const moduleBuilder = storeBuilder.module('editor-sessions', makeInitialState());
/** The store for editor sessions. */
export var EditorSessionsStore;
(function (EditorSessionsStore) {
    /** Get sessions for a specific assignment and user. */
    EditorSessionsStore.getSessions = moduleBuilder.read(state => 
    /**
     * Gets the session given an assignment and user from the state.
     *
     * @param assignmentId Assignment ID.
     * @param userId User ID.
     * @returns The given session.
     */
    (assignmentId, userId) => Maybe.fromNullable(state.sessions[assignmentId]?.[userId]), 'getSessions');
    /** Get loading state for a specific assignment and user. */
    EditorSessionsStore.getLoadingState = moduleBuilder.read(state => 
    /**
     * Gets the loading state from a given state.
     *
     * @param assignmentId Assignment ID.
     * @param userId User ID.
     * @returns The loading state.
     */
    (assignmentId, userId) => {
        const sessions = EditorSessionsStore.getSessions()(assignmentId, userId);
        const isLoading = state.loaders[assignmentId]?.[userId] != null;
        if (isLoading) {
            return { state: 'loading', data: sessions.map(s => s.data).orDefault([]) };
        }
        if (sessions.isNothing()) {
            return { state: 'not-started', data: [] };
        }
        if (sessions.chain(s => Maybe.fromNullable(s.nextToken)).isJust()) {
            return { state: 'loaded', data: sessions.extract().data };
        }
        return { state: 'loaded-all', data: sessions.extract().data };
    }, 'getLoadingState');
    /** Set sessions for a specific assignment and user. */
    const commitSessions = moduleBuilder.commit((state, { assignmentId, userId, sessions, }) => {
        if (state.sessions[assignmentId] === undefined) {
            utils.vueSet(state.sessions, assignmentId, {});
        }
        utils.vueSet(state.sessions[assignmentId], userId, sessions);
    }, 'commitSessions');
    /** Update the loader for a specific assignment and user. */
    const commitLoader = moduleBuilder.commit((state, { assignmentId, userId, loader, }) => {
        if (state.loaders[assignmentId] === undefined) {
            utils.vueSet(state.loaders, assignmentId, {});
        }
        if (loader === undefined) {
            utils.vueDelete(state.loaders[assignmentId], userId);
        }
        else {
            utils.vueSet(state.loaders[assignmentId], userId, loader);
        }
    }, 'commitLoader');
    /** Clear all reset state. */
    EditorSessionsStore.commitClearState = moduleBuilder.commit(state => {
        Object.assign(state, makeInitialState());
    }, 'commitClearState');
    /**
     * Convert API response to store state.
     *
     * @param result The API response to convert.
     * @returns The converted state.
     */
    function convertApiResponse(result) {
        return {
            data: result.editorSessions.map(session => Submission.fromSmithy(session, FileTree.fromResponse([]))),
            nextToken: result.nextToken,
        };
    }
    /** Load sessions for a specific assignment and user. */
    EditorSessionsStore.loadSessions = moduleBuilder.dispatch(async ({ state }, { assignmentId, userId, currentSession, loadFn, }) => {
        let foundCurrentSession = false;
        let nextToken;
        let sessions = state.sessions[assignmentId]?.[userId] ?? {
            data: [],
            nextToken: undefined,
        };
        await utils.storeLoadWithMaybeForce({
            value: EditorSessionsStore.getSessions()(assignmentId, userId),
            force: false,
            load: async () => {
                // Here we load sessions until we find the actively selected sesssion
                do {
                    // eslint-disable-next-line no-await-in-loop
                    const response = await loadFn(new ListEditorSessionsCommand({
                        assignmentId: assignmentId.toString(),
                        userId: userId.toString(),
                        nextToken,
                    }));
                    const newSessions = convertApiResponse(response);
                    sessions = {
                        data: [...sessions.data, ...newSessions.data],
                        nextToken: response.nextToken,
                    };
                    foundCurrentSession = newSessions.data.some(session => session.id === currentSession.id);
                    nextToken = response.nextToken;
                } while (!foundCurrentSession && nextToken !== undefined);
                commitSessions({ assignmentId, userId, sessions });
                return sessions;
            },
            loader: Maybe.fromNullable(state.loaders[assignmentId]?.[userId]),
            addLoader: loader => commitLoader({ assignmentId, userId, loader }),
            removeLoader: () => commitLoader({ assignmentId, userId, loader: undefined }),
        });
    }, 'loadSessions');
    /** Load more sessions for a specific assignment and user. */
    EditorSessionsStore.loadMore = moduleBuilder.dispatch(async (_context, { assignmentId, userId, loadFn, }) => {
        const currentSessions = EditorSessionsStore.getSessions()(assignmentId, userId).extract();
        if (currentSessions?.nextToken === undefined) {
            return;
        }
        const result = await loadFn(new ListEditorSessionsCommand({
            assignmentId: assignmentId.toString(),
            userId: userId.toString(),
            nextToken: currentSessions.nextToken,
        }));
        const newSessions = result.editorSessions.map(session => Submission.fromSmithy(session, FileTree.fromResponse([])));
        commitSessions({
            assignmentId,
            userId,
            sessions: {
                data: [...currentSessions.data, ...newSessions],
                nextToken: result.nextToken,
            },
        });
    }, 'loadMore');
    /** Reset the store when the user changes. */
    utils.signal.AFTER_USER_CHANGED.register(async () => {
        EditorSessionsStore.commitClearState();
    });
})(EditorSessionsStore || (EditorSessionsStore = {}));
